import React from "react";
import type { DropzoneState } from "react-dropzone";
import { Button } from "../../button";
import { type UseLoading, useLoading } from "../../hooks";
import { Icon } from "../../icons";
import { useTranslation } from "../../localization";
import { Menu } from "../../menu";
import { ConfirmModal, useAsyncModal } from "../../modal";
import { Tooltip } from "../../tooltip";
import { useFileExplorerContext } from "./context";
import { FileExplorerCreateFolder } from "./file-explorer-create-folder";
import { type FileExplorerMenuExtension, selectionPasses } from "./shared";

/** Props Interface */
interface FileExplorerMenuProps {
	dropzone: DropzoneState;
	uploadLoader: UseLoading;
	isLoading: boolean;
	menu?: FileExplorerMenuExtension;
}

export function FileExplorerMenu({
	dropzone,
	uploadLoader,
	isLoading,
	menu: MenuExtension,
}: FileExplorerMenuProps) {
	/** Context */
	const context = useFileExplorerContext();
	const {
		selectedFiles,
		refetch,
		onDeleteFiles,
		onDownloadFiles,
		handleOnAfterFileAction,
		filesById,
		canUpload,
		canDeleteFile,
	} = context;

	/** Hooks */
	const deleteLoader = useLoading();
	const downloadLoader = useLoading();
	const { t } = useTranslation();
	const asyncModal = useAsyncModal();

	/** Computed */
	const totalSelection = selectedFiles;
	const hasSelection = totalSelection.length > 0;
	// Removing reaches only what the caller uploaded, so the entry is closed while the selection
	// holds one file that is somebody else's — resolved against every file the explorer has, not
	// against the ones the search box currently shows.
	//
	// The route refuses a mixed batch whole and names the files it refused, so this is the affordance
	// rather than the guard: it keeps the user from arming a deletion that would come back as a
	// message instead of doing anything.
	const mayDeleteSelection = selectionPasses(totalSelection, filesById, canDeleteFile);

	/** Functions */
	async function handleOnDeleteFiles() {
		try {
			deleteLoader.start();
			const isConfirmed = await asyncModal.confirm();
			if (!isConfirmed) return;
			await onDeleteFiles(totalSelection);
			await handleOnAfterFileAction();
		} catch (error) {
			console.error("Error deleting files:", error);
		} finally {
			deleteLoader.stop();
		}
	}

	async function handleOnDownloadFiles() {
		try {
			downloadLoader.start();
			await onDownloadFiles(totalSelection);
			await handleOnAfterFileAction();
		} catch (error) {
			console.error("Error downloading files:", error);
		} finally {
			downloadLoader.stop();
		}
	}

	/** Render */
	return (
		<React.Fragment>
			<div className="h-stack">
				{MenuExtension && <MenuExtension context={context} />}
				{hasSelection && <FileExplorerCreateFolder />}
				{canUpload && (
					<Tooltip label="Drag Folder on File Explorer to upload folders">
						<Button
							icon={<Icon name="Plus" />}
							variant="primary"
							onClick={() => {
								dropzone.open();
							}}
							isLoading={uploadLoader.isLoading}
						>
							{t("file_upload.upload_files")}
						</Button>
					</Tooltip>
				)}
				<Button
					icon={<Icon name="RefreshCw" />}
					onClick={refetch}
					isLoading={isLoading}
				/>
				<input
					type="file"
					className="hidden"
					{...dropzone.getInputProps()}
				/>
				<Menu
					items={() => [
						{
							label: t("files.download_n_files", { count: `${totalSelection.length}` }),
							icon: () => <Icon name="Download" />,
							onClick: handleOnDownloadFiles,
							isDisabled: !hasSelection,
						},
						{
							label: t("files.delete_n_files", { count: `${totalSelection.length}` }),
							icon: () => <Icon name="Trash2" />,
							onClick: handleOnDeleteFiles,
							isDisabled: !hasSelection || !mayDeleteSelection,
							isDanger: true,
						},
					]}
				>
					<Menu.Button
						variant="white"
						size="xs"
					/>
				</Menu>
			</div>
			<ConfirmModal
				controller={asyncModal}
				message={t("files.delete_files_confirm_message", {
					count: `${totalSelection.length}`,
				})}
			/>
		</React.Fragment>
	);
}
